import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BookOpen, Clock, RefreshCcw, Sparkles, ChevronRight, ChevronDown } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/* Smart Daily Planner card: New Memorization · Recent Memorization · Long-Term Revision.

   C1 — this used to always render fully open: header, AI coach message, the
   3 plan rows, a pacing forecast ("At this pace: done in ~N days"), and an
   "I have less time today" link that expanded into plan-adjustment buttons
   (revision-only / skip-new / defer / full). Collapsed into a single toggle
   button now — expanding it shows only the plan itself and the AI's
   comment, per the instruction. The forecast and "I have less time today"
   (and the adjust-plan buttons under it) are dropped entirely, not just
   hidden while collapsed — that was the only place in the app that called
   POST /plan/adjust (revision_only/skip_new/defer/full), so that capability
   has no UI entry point left; the backend route itself is untouched and
   harmless left as-is, just orphaned. Flagged here since it's a real,
   working feature being retired, not just a visual tweak. */
export default function TodayPlanCard() {
  const { t } = useLanguage();
  const [plan, setPlan] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = () => { api.get("/plan/today").then((r) => setPlan(r.data)).catch(() => {}); };
  useEffect(load, []);

  if (!plan) return <section className="hifz-card h-40 animate-pulse-soft bg-sand-200/40" data-testid="today-plan-loading" />;

  const nm = plan.new_memorization;
  const rows = [
    {
      icon: BookOpen, title: t("plan.newMemorization"),
      detail: nm ? t("plan.newDetail", { surah: nm.surah_name, start: nm.ayah_start, end: nm.ayah_end, count: nm.count }) : plan.mode !== "full" ? t("plan.skippedToday") : t("plan.allMemorized"),
      mins: plan.est_minutes.new,
      to: nm ? `/app/memorize/${nm.surah}/${nm.ayah_start}` : null,
      cta: t("plan.memorize"), testid: "plan-new",
    },
    {
      icon: Clock, title: t("plan.recentMemorization"),
      detail: t(plan.recent_review.count === 1 ? "plan.toReviewOne" : "plan.toReviewOther", { count: plan.recent_review.count }),
      mins: plan.est_minutes.recent,
      to: plan.recent_review.count > 0 ? "/app/revision" : null,
      cta: t("plan.review"), testid: "plan-recent",
    },
    {
      icon: RefreshCcw, title: t("plan.longTermRevision"),
      detail: t(plan.long_term.count === 1 ? "plan.dueOne" : "plan.dueOther", { count: plan.long_term.count }),
      mins: plan.est_minutes.long_term,
      to: plan.long_term.count > 0 ? "/app/revision" : null,
      cta: t("plan.revise"), testid: "plan-long",
    },
  ];

  return (
    <section className="hifz-card" data-testid="today-plan-card">
      {/* The toggle button covers title + total time + chevron only —
          "Adjust goal" stays a separate sibling link (not nested inside the
          button, which would be invalid markup) so tapping it navigates
          instead of just toggling the card.

          A bare muted-gray chevron sitting inline after some text read as
          decorative, not interactive — nothing about it looked pressable.
          Now the whole row gets a hover/press background (so it visually
          responds like every other tappable row in the app) and the
          chevron sits inside a solid filled circle instead of floating
          free, which is what actually reads as "this is a control" rather
          than "this is a small icon near some text." */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2.5 min-w-0 text-left -mx-2 -my-1.5 px-2 py-1.5 rounded-xl hover:bg-sand-200/70 active:bg-sand-200 transition"
          aria-expanded={expanded}
          data-testid="today-plan-toggle"
        >
          <h2 className="font-display font-semibold text-xl text-forest-700">{t("plan.title")}</h2>
          <span className="text-xs text-muted-foreground shrink-0">{t("plan.minutesTotal", { minutes: plan.est_minutes.total })}</span>
          <span className="h-6 w-6 rounded-full bg-forest-700 grid place-items-center shrink-0">
            {/* grid place-items-center centers the icon's own box perfectly,
                but the chevron glyph itself sits slightly high within that
                box, reading as off-center — nudged down a hair to actually
                look centered. */}
            <ChevronDown className={`h-3.5 w-3.5 translate-y-px text-sand-100 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </span>
        </button>
        <Link to="/app/revision" className="text-xs text-forest-700 hover:underline shrink-0" data-testid="plan-adjust-goal-link">{t("plan.adjustGoal")}</Link>
      </div>

      {expanded && (
        <>
          <div className="mt-3 rounded-xl border-l-4 border-gold-500 bg-sand-200/60 p-3 text-sm text-foreground/85 flex gap-2.5" data-testid="plan-coach-message">
            <Sparkles className="h-4 w-4 shrink-0 text-gold-600 mt-0.5" />
            {plan.coach_message}
          </div>

          <div className="mt-4 space-y-2.5">
            {rows.map((r) => (
              <div key={r.title} className="flex items-center justify-between gap-3 rounded-xl border border-sand-300 bg-sand-200/30 px-4 py-3" data-testid={r.testid}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-white grid place-items-center text-forest-700 shrink-0">
                    <r.icon className="h-4.5 w-4.5 h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-display font-semibold text-forest-700">{r.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{r.detail} · {t("plan.minutes", { minutes: r.mins })}</div>
                  </div>
                </div>
                {r.to && (
                  <Link to={r.to} className="btn-ghost text-xs px-3 py-1.5 shrink-0" data-testid={`${r.testid}-cta`}>
                    {r.cta} <ChevronRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
